import { logger } from '@common';
import { BoostPostStep, FormValueSetter, CreatedResourcesTracker, PartialFailureContext } from '../types/boostPost';

/**
 * Creates error handling utilities for boost post operations
 */
export const createErrorHandler = (showErrorMessage: (message: string) => void, loggerInstance: typeof logger) => {
  /**
   * Handles partial failure by updating form state with successfully created resources
   */
  const handlePartialFailure = (createdResources: CreatedResourcesTracker, setFormValue?: FormValueSetter): void => {
    if (!setFormValue) {
      loggerInstance.warn('No setFormValue function available for form updates after partial failure');
      return;
    }

    loggerInstance.info('Handling partial failure, updating form state with created resources:', {
      createdResources,
    });

    if (createdResources.campaignId) {
      loggerInstance.info('Setting campaign to existing after partial failure:', createdResources.campaignId);
      setFormValue('campaign.type', 'existing');
      setFormValue('campaign.campaignId', createdResources.campaignId);
    }

    if (createdResources.adSetId) {
      loggerInstance.info('Setting ad set to existing after partial failure:', createdResources.adSetId);
      setFormValue('adSet.type', 'existing');
      setFormValue('adSet.id', createdResources.adSetId);
    }
  };

  /**
   * Handles creation errors with consistent messaging and logging
   */
  const handleCreationError = (step: BoostPostStep, error: unknown, defaultMessage: string): string => {
    const errorMessage = safeExtractErrorMessage(error, defaultMessage);

    showErrorMessage(errorMessage);

    loggerInstance.error(`${step} creation failed:`, {
      step,
      error: errorMessage,
      errorObject: error,
    });

    return errorMessage;
  };

  /**
   * Handles contextual errors with form state updates
   */
  const handleContextualError = (context: PartialFailureContext): string => {
    const defaultMessage = `Error creating ${context.step}`;

    loggerInstance.error('Contextual error in boost post operation:', {
      step: context.step,
      createdResources: context.createdResources,
      hasFormSetter: !!context.setFormValue,
    });

    if (context.setFormValue) {
      handlePartialFailure(context.createdResources, context.setFormValue);
    }

    showErrorMessage(defaultMessage);
    return defaultMessage;
  };

  /**
   * Extracts error message from various error types
   */
  const extractErrorMessage = (error: unknown, fallback: string): string => {
    return safeExtractErrorMessage(error, fallback);
  };

  /**
   * Logs successful operations
   */
  const logSuccess = (step: BoostPostStep, resourceId: string, additionalContext?: Record<string, unknown>): void => {
    loggerInstance.info(`${step} creation successful:`, {
      step,
      resourceId,
      ...additionalContext,
    });
  };

  return {
    handlePartialFailure,
    handleCreationError,
    handleContextualError,
    extractErrorMessage,
    logSuccess,
  };
};

/**
 * Type guard to check if an object has an error message
 */
export const hasErrorMessage = (error: unknown): error is { message: string } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: string }).message === 'string'
  );
};

/**
 * Safely extracts error message from various error types
 */
export const safeExtractErrorMessage = (error: unknown, fallback = 'Unknown error'): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (hasErrorMessage(error)) {
    return error.message;
  }

  // Handle GraphQL errors
  if (
    typeof error === 'object' &&
    error !== null &&
    'graphQLErrors' in error &&
    Array.isArray((error as { graphQLErrors: unknown[] }).graphQLErrors)
  ) {
    const graphQLErrors = (error as { graphQLErrors: unknown[] }).graphQLErrors;
    if (graphQLErrors.length > 0 && hasErrorMessage(graphQLErrors[0])) {
      return graphQLErrors[0].message;
    }
  }

  return fallback;
};
